/**
 * Status → tone / label mapping and roll-up helpers. Pure, dependency-free —
 * extracted from `App.tsx` so the (subtle) status precedence and percentage
 * formatting are testable in isolation and reusable across the per-concern
 * component modules.
 */
import type { BucketSeverity } from './bucket-severity';
import type { ComponentStatus } from './status-types';

export type Tone = 'ok' | 'warn' | 'major' | 'down' | 'none';

export function statusTone(status: ComponentStatus): Tone {
  switch (status) {
    case 'operational':
    case 'maintenance':
      return 'ok';
    case 'degraded_performance':
      return 'warn';
    case 'partial_outage':
    case 'major_outage':
      return 'down';
    case 'no_data':
      return 'none';
  }
}

export function statusLabel(status: ComponentStatus): string {
  switch (status) {
    case 'operational':
      return 'Operational';
    case 'degraded_performance':
      return 'Degraded performance';
    case 'partial_outage':
      return 'Partial outage';
    case 'major_outage':
      return 'Major outage';
    case 'maintenance':
      return 'Maintenance';
    case 'no_data':
      return 'No data';
  }
}

export const TONE_BAR: Record<Tone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  major: 'bg-major',
  down: 'bg-down',
  none: 'bg-[rgb(var(--bar-empty))]',
};

export function bucketTone(status: BucketSeverity): Tone {
  if (status === 'ok') return 'ok';
  if (status === 'warn') return 'warn';
  if (status === 'major') return 'major';
  if (status === 'down') return 'down';
  return 'none';
}

const STATUS_RANK: Record<ComponentStatus, number> = {
  operational: 0,
  degraded_performance: 1,
  maintenance: 2,
  partial_outage: 3,
  major_outage: 4,
  no_data: -1,
};

export function deriveOverall(
  statuses: readonly ComponentStatus[],
): ComponentStatus {
  if (statuses.length === 0) return 'no_data';
  let worst: ComponentStatus = 'operational';
  let worstRank = STATUS_RANK.operational;
  for (const s of statuses) {
    const rank = STATUS_RANK[s];
    if (rank > worstRank) {
      worst = s;
      worstRank = rank;
    }
  }
  return worst;
}

export function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  if (value <= 0) return '—';
  // TRUNCATE, never round. `toFixed(2)` rounds 99.9993% up to "100.00%", so a
  // window that demonstrably had failures printed as a perfect score — the
  // headline contradicted the `11 down` right next to it. Atlassian and Stripe
  // truncate for the same reason (Stripe hard-caps its displayed uptime at
  // 99.999% and never prints 100%). Only an exact 1.0 reads "100.00%".
  //
  // The epsilon absorbs IEEE-754 artefacts: 0.83 * 10000 is 8299.999999999999,
  // which a bare Math.floor would render as "82.99%".
  const hundredths = Math.floor(value * 10000 + 1e-6);
  return `${(hundredths / 100).toFixed(2)}%`;
}
